import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { ClassroomInfo, ClassroomStatus } from "../classroom/classroomSlice";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { classroomDelete } from "../classroom/classroomInsUpdSlice";

export interface SchoolClassroomsState {
  state: LoadStateInfo;
  schoolId?: string;
  list?: ClassroomInfo[];
}

const initialState: SchoolClassroomsState = {
  state: notStarted(),
};


export interface RetrieveSchoolClassroomsRequest {
  schoolId: string;
}
export const retrieveSchoolClassrooms = createAsyncThunk(
  "retrieveSchoolClassrooms",
  async (param: RetrieveSchoolClassroomsRequest, thunkAPI) => {
    console.debug(`retrieve classrooms for school ${param.schoolId}`);

    const response = await fetch(`${getBackendUrl()}/api/school/${param.schoolId}/classroom?getTeachers=true`, {
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
      }
    });

    if(response.ok) {
      return await response.json();
    } else {
      return thunkAPI.rejectWithValue(failedResponse( response ));
    }
  }
);


export const schoolClassroomsSlice = createSlice({
  name: "schoolClassrooms",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    clean: (state) => {
      state.state = notStarted();
      state.list = undefined;
    }
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(retrieveSchoolClassrooms.pending, (state, action) => {
        state.state =  loading();
        state.list = undefined;
        state.schoolId = action.meta.arg.schoolId;
      })
      .addCase(retrieveSchoolClassrooms.fulfilled, (state, action) => {
        console.debug(`${action.type}`);
        state.state =  loaded();
        state.list = action.payload;
        state.list?.sort((a, b) => {
            const aStatus = a?.classroomStatus ?? ClassroomStatus.ENABLED;
            const bStatus = b?.classroomStatus ?? ClassroomStatus.ENABLED;

            if(aStatus !== bStatus) {
                // console.debug("comparing statuses", aStatus, bStatus);
                if (aStatus === ClassroomStatus.ENABLED) {
                    return -1;
                }
                if (bStatus === ClassroomStatus.ENABLED) {
                    return 1;
                }
                if (aStatus === ClassroomStatus.NOT_READY) {
                    return -1;
                }
                if (bStatus === ClassroomStatus.NOT_READY) {
                    return 1;
                }
                // console.debug("wtf", aStatus, bStatus);
            }

            // console.debug("status are equal", aStatus, bStatus);

            const aYear = a?.year ?? "0";
            const bYear = b?.year ?? "0";

            if(aYear !== bYear) {
                // console.debug("comparing years", aYear, bYear);
                return -aYear.localeCompare(bYear);
            }

            return (a?.name || "").localeCompare(b?.name || "");
        });
      })
      .addCase(retrieveSchoolClassrooms.rejected, (state, action) => {
        state.state = error(action);
      })
      .addCase(classroomDelete.fulfilled, (state, /* action */) => {
        state.state = notStarted();
      })
      ;
    },
});

// export const { } = schoolClassroomsSlice.actions;

export const selectSchoolClassrooms = (state: RootState) => state.schoolClassrooms;

export default schoolClassroomsSlice.reducer;
